// Pure monthly billing/analytics helpers. No React, no Supabase.
//
// Revenue model (kept consistent with the rest of the app): every time entry's
// euro value is minutes / 60 * the logging employee's CURRENT hourly_rate. This
// is an approximation when a rate changed mid-history; it matches projectUsage
// in DataContext and is good enough for monthly billing. A future migration
// could snapshot the rate onto each entry for exactness.

import { durationMin, pad } from './format.js';

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export function monthLabel(year, month /* 0-based */) {
  return `${MONTH_NAMES[month]} ${year}`;
}

export function monthKey(year, month) {
  return `${year}-${pad(month + 1)}`;
}

// Inclusive ISO date bounds for a month, e.g. {start:'2026-06-01', end:'2026-06-30'}
export function monthRange(year, month) {
  const last = new Date(year, month + 1, 0).getDate();
  return { start: `${year}-${pad(month + 1)}-01`, end: `${year}-${pad(month + 1)}-${pad(last)}` };
}

export function addMonths(year, month, delta) {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

function entryMinutes(te) {
  return Math.max(0, durationMin(te.from_time, te.to_time));
}

// Whether an entry counts as billable. Project work follows the PROJECT's
// billable flag (the admin toggles it in the Auswertung); entries without a
// project (meetings / support) fall back to their own billable flag.
function isBillable(te, project) {
  if (project) return project.billable !== false;
  return te.billable !== false;
}

// Human label for an entry without a project (meeting / support / other).
function nonProjectLabel(te) {
  if (te.kind === 'meeting') return 'Besprechungen';
  if (te.kind === 'support') return 'Support / Intern';
  return 'Ohne Projekt';
}

const NO_CUSTOMER = '__none__';

// Main aggregation. Returns everything the Auswertung page and CSV need.
//
//   timeEntries  – all entries (we filter by month here)
//   profiles     – for names + hourly_rate
//   projects     – id → title/customer
//   customers    – id → name
//   opts.year, opts.month (0-based), opts.filter
export function buildMonthlyReport(timeEntries, profiles, projects, customers, opts) {
  const { year, month, filter = 'all' } = opts;
  const { start, end } = monthRange(year, month);

  const profileById = new Map(profiles.map(p => [p.id, p]));
  const projectById = new Map(projects.map(p => [p.id, p]));
  const customerById = new Map(customers.map(c => [c.id, c]));

  // Aggregation buckets keyed by composite ids.
  const customerAgg = new Map();   // custKey -> { customer, minutes, euro, projects: Map }
  const employeeAgg = new Map();   // userId  -> { profile, minutes, euro }
  const lineItems = [];            // flat rows for CSV (cust × project × user)
  const lineKey = new Map();       // dedup key -> lineItem

  let totalMinutes = 0, totalEuro = 0;
  let billableMinutes = 0, internalMinutes = 0, billableEuro = 0, internalEuro = 0;
  let invoicedEuro = 0; // davon bereits abgerechnet (Projekt-Flag invoiced)

  for (const te of timeEntries) {
    if (!te.entry_date || te.entry_date < start || te.entry_date > end) continue;

    const min = entryMinutes(te);
    if (min === 0) continue;

    // Resolve project first — billability is decided at the project level.
    const project = te.project_id ? projectById.get(te.project_id) : null;
    const billable = isBillable(te, project);
    // "Abgerechnet" gilt nur für abrechenbare Projektarbeit, die als invoiced
    // markiert ist. Interne/projektlose Arbeit kann nie "abgerechnet" sein.
    const invoiced = billable && !!project?.invoiced;
    if (filter === 'billable' && !billable) continue;
    if (filter === 'internal' && billable) continue;

    const prof = profileById.get(te.user_id);
    const rate = Number(prof?.hourly_rate ?? 120);
    const euro = (min / 60) * rate;

    // Resolve customer label.
    const custId = project?.customer_id || te.customer_id || NO_CUSTOMER;
    const customer = custId === NO_CUSTOMER ? null : customerById.get(custId);
    const custKey = custId || NO_CUSTOMER;

    const projKey = project ? `p:${project.id}` : `np:${te.kind || 'none'}`;
    const projLabel = project ? project.title : nonProjectLabel(te);

    // Customer bucket
    let cAgg = customerAgg.get(custKey);
    if (!cAgg) {
      cAgg = { id: custKey, customer, minutes: 0, euro: 0, billableEuro: 0, internalEuro: 0, invoicedEuro: 0, projects: new Map() };
      customerAgg.set(custKey, cAgg);
    }
    cAgg.minutes += min;
    cAgg.euro += euro;
    if (billable) cAgg.billableEuro += euro; else cAgg.internalEuro += euro;
    if (invoiced) cAgg.invoicedEuro += euro;
    let pAgg = cAgg.projects.get(projKey);
    if (!pAgg) {
      pAgg = { id: projKey, projectId: project?.id || null, label: projLabel, minutes: 0, euro: 0 };
      cAgg.projects.set(projKey, pAgg);
    }
    pAgg.minutes += min;
    pAgg.euro += euro;

    // Employee bucket
    let eAgg = employeeAgg.get(te.user_id);
    if (!eAgg) {
      eAgg = { profile: prof || { id: te.user_id, full_name: 'Unbekannt' }, rate, minutes: 0, euro: 0, billableEuro: 0, internalEuro: 0, invoicedEuro: 0 };
      employeeAgg.set(te.user_id, eAgg);
    }
    eAgg.minutes += min;
    eAgg.euro += euro;
    if (billable) eAgg.billableEuro += euro; else eAgg.internalEuro += euro;
    if (invoiced) eAgg.invoicedEuro += euro;

    // Flat CSV line item (cust × project × user × billable flag)
    const liKey = `${custKey}|${projKey}|${te.user_id}|${billable}`;
    let li = lineKey.get(liKey);
    if (!li) {
      li = {
        customerName: customer?.name || (custKey === NO_CUSTOMER ? 'Ohne Kunde' : '—'),
        projectLabel: projLabel,
        employeeName: prof?.full_name || 'Unbekannt',
        rate,
        billable,
        minutes: 0,
        euro: 0,
      };
      lineKey.set(liKey, li);
      lineItems.push(li);
    }
    li.minutes += min;
    li.euro += euro;

    totalMinutes += min;
    totalEuro += euro;
    if (billable) { billableMinutes += min; billableEuro += euro; }
    else { internalMinutes += min; internalEuro += euro; }
    if (invoiced) invoicedEuro += euro;
  }

  // Materialize + sort (descending euro).
  const perCustomer = [...customerAgg.values()]
    .map(c => ({
      ...c,
      projects: [...c.projects.values()].sort((a, b) => b.euro - a.euro),
    }))
    .sort((a, b) => (b.billableEuro - a.billableEuro) || (b.euro - a.euro));

  const perEmployee = [...employeeAgg.values()]
    .sort((a, b) => (b.billableEuro - a.billableEuro) || (b.euro - a.euro));

  lineItems.sort((a, b) =>
    a.customerName.localeCompare(b.customerName) ||
    a.projectLabel.localeCompare(b.projectLabel) ||
    a.employeeName.localeCompare(b.employeeName));

  return {
    year, month, filter,
    perCustomer, perEmployee, lineItems,
    totals: { minutes: totalMinutes, euro: totalEuro, billableMinutes, internalMinutes, billableEuro, internalEuro, invoicedEuro, openEuro: billableEuro - invoicedEuro },
  };
}

// Projects whose completed_at falls inside the given month, with on-time and
// budget analysis. `projectUsage` is the lifetime usage map from DataContext
// ({ [projectId]: { minutes, euro } }).
export function completedInMonth(projects, customers, projectUsage, opts) {
  const { year, month } = opts;
  const { start, end } = monthRange(year, month);
  const customerById = new Map(customers.map(c => [c.id, c]));

  return projects
    .filter(p => {
      if (p.status !== 'done' || !p.completed_at) return false;
      const iso = p.completed_at.slice(0, 10);
      return iso >= start && iso <= end;
    })
    .map(p => {
      const usage = projectUsage[p.id] || { minutes: 0, euro: 0 };
      const completedIso = p.completed_at.slice(0, 10);
      // Compare against the ORIGINAL deadline so deadline pushes don't hide lateness.
      const refDeadline = p.original_deadline || p.deadline || null;
      let daysLate = null, onTime = null;
      if (refDeadline) {
        daysLate = Math.round((new Date(completedIso) - new Date(refDeadline)) / 86400000);
        onTime = daysLate <= 0;
      }
      const budgetPct = p.budget_minutes ? Math.round((usage.minutes / p.budget_minutes) * 100) : null;
      return {
        project: p,
        customer: p.customer_id ? customerById.get(p.customer_id) || null : null,
        completedAt: completedIso,
        deadline: refDeadline,
        deadlineWasPushed: !!(p.original_deadline && p.deadline && p.original_deadline !== p.deadline),
        daysLate,
        onTime,
        usedMinutes: usage.minutes,
        budgetMinutes: p.budget_minutes || null,
        budgetPct,
        overBudget: budgetPct != null && budgetPct > 100,
      };
    })
    .sort((a, b) => (a.completedAt < b.completedAt ? 1 : -1));
}

// Billable projects with revenue in the report's month that haven't been marked
// invoiced yet — the basis for the end-of-month reminder.
export function unbilledFromReport(report, projects) {
  const byId = new Map(projects.map(p => [p.id, p]));
  const out = [];
  for (const c of report.perCustomer) {
    for (const p of c.projects) {
      if (!p.projectId || p.euro <= 0) continue;
      const proj = byId.get(p.projectId);
      if (!proj || proj.billable === false || proj.invoiced) continue;
      out.push({
        project: proj,
        customer: c.customer,
        customerName: c.customer?.name || 'Ohne Kunde',
        minutes: p.minutes,
        euro: p.euro,
      });
    }
  }
  return out.sort((a, b) => b.euro - a.euro);
}

// ---------------------------------------------------------------------------
// CSV — one compact monthly file. Semicolon-delimited, decimal comma, UTF-8 BOM
// so German Excel opens it cleanly.
// ---------------------------------------------------------------------------
function csvCell(v) {
  const s = String(v ?? '');
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function num(n) {
  return (Math.round(n * 100) / 100).toFixed(2).replace('.', ',');
}
function hours(min) {
  return (Math.round((min / 60) * 100) / 100).toFixed(2).replace('.', ',');
}

const FILTER_LABEL = { all: 'Alle Stunden', billable: 'Nur abrechenbar', internal: 'Nur intern' };

export function buildMonthlyCsv(report) {
  const { perEmployee, lineItems, totals, year, month, filter } = report;
  const rows = [];
  const push = (...cells) => rows.push(cells.map(csvCell).join(';'));

  push(`Monatsauswertung ${monthLabel(year, month)}`);
  push(`Filter:`, FILTER_LABEL[filter] || filter);
  push('');

  push('Positionen');
  push('Kunde', 'Projekt', 'Mitarbeiter', 'Stunden', 'Stundensatz (EUR)', 'Umsatz (EUR)', 'Abrechenbar');
  for (const li of lineItems) {
    push(li.customerName, li.projectLabel, li.employeeName,
      hours(li.minutes), num(li.rate), num(li.euro), li.billable ? 'ja' : 'nein');
  }
  push('');

  push('Umsatz pro Mitarbeiter');
  push('Mitarbeiter', 'Stunden', 'Umsatz (EUR)');
  for (const e of perEmployee) {
    push(e.profile.full_name || '—', hours(e.minutes), num(e.euro));
  }
  push('');

  push('Gesamt');
  push('Stunden gesamt', hours(totals.minutes));
  push('davon abrechenbar', hours(totals.billableMinutes));
  push('davon intern', hours(totals.internalMinutes));
  push('Umsatz abrechenbar (EUR)', num(totals.billableEuro));
  push('Wert intern / nicht abrechenbar (EUR)', num(totals.internalEuro));
  push('Leistungswert gesamt (EUR)', num(totals.euro));

  return rows.join('\r\n');
}

// Trigger a browser download of CSV text. No-op outside a browser.
export function downloadCsv(filename, text) {
  if (typeof document === 'undefined') return;
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
